<template>
  <div class="validacion-container">

    <!-- ── Barra de acciones ──────────────────────────────────── -->
    <div class="d-flex align-items-center gap-2 mb-3">
      <button
        class="btn btn-primary btn-sm d-flex align-items-center gap-1"
        @click="validarTodo"
        :disabled="validandoTodo"
      >
        <span v-if="validandoTodo" class="spinner-border spinner-border-sm"></span>
        <span v-else class="material-icons" style="font-size:16px">verified</span>
        {{ validandoTodo ? 'Validando todo...' : 'Validar todo' }}
      </button>
      <button
        class="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
        @click="limpiarTodo"
      >
        <span class="material-icons" style="font-size:16px">refresh</span>
        Limpiar
      </button>
      <!-- Resumen global -->
      <div class="ms-auto d-flex gap-1">
        <span v-for="v in validaciones" :key="v.id"
          class="badge d-flex align-items-center gap-1"
          :class="badgeClase(v)"
          :title="v.label"
        >
          <span class="material-icons" style="font-size:13px">{{ iconoEstado(v) }}</span>
          {{ v.sigla }}
        </span>
      </div>
    </div>

    <!-- ── Acordeón de validaciones ───────────────────────────── -->
    <div class="accordion" id="acordeonValidaciones">
      <div
        v-for="v in validaciones"
        :key="v.id"
        class="accordion-item border-0 mb-2 rounded overflow-hidden shadow-sm"
      >
        <!-- Header del acordeón -->
        <h2 class="accordion-header">
          <button
            class="accordion-button py-2 px-3 d-flex align-items-center gap-2"
            :class="[headerClase(v), { collapsed: tabActiva !== v.id }]"
            type="button"
            @click="toggleTab(v.id)"
          >
            <!-- Ícono estado -->
            <span
              class="estado-badge d-flex align-items-center justify-content-center rounded-circle me-1"
              :class="estadoBadgeClase(v)"
              style="width:28px;height:28px;min-width:28px"
            >
              <span class="material-icons" style="font-size:16px">
                {{ iconoEstado(v) }}
              </span>
            </span>

            <!-- Título -->
            <span class="fw-bold small">{{ v.label }}</span>

            <!-- Estado texto -->
            <span class="ms-2 badge small" :class="badgeClase(v)">
              {{ textoEstado(v) }}
            </span>

            <!-- Botón validar individual -->
            <button
              class="btn btn-sm ms-auto me-2 d-flex align-items-center gap-1"
              :class="botonIndividualClase(v)"
              @click.stop="validarIndividual(v.id)"
              :disabled="v.validando"
              style="font-size:11px; padding:2px 8px"
            >
              <span v-if="v.validando"
                class="spinner-border spinner-border-sm"
                style="width:10px;height:10px">
              </span>
              <span v-else class="material-icons" style="font-size:13px">
                play_arrow
              </span>
              {{ v.validando ? '...' : 'Validar' }}
            </button>

            <span class="material-icons" style="font-size:18px;opacity:0.5">
              {{ tabActiva === v.id ? 'expand_less' : 'expand_more' }}
            </span>
          </button>
        </h2>

        <!-- Body del acordeón -->
        <div
          class="accordion-collapse"
          :class="{ show: tabActiva === v.id }"
        >
          <div class="accordion-body p-0">
            <!-- CV -->
            <CVpdfValidador
              v-if="v.id === 'cv'"
              ref="refCV"
              :idTramite="p_idTramite"
              :numero="p_numero"
              :autoValidar="false"
              @resultado="r => onResultado('cv', r)"
            />
            <!-- CC -->
            <CCpdfValidador
              v-if="v.id === 'cc'"
              ref="refCC"
              :idTramite="p_idTramite"
              :numero="p_numero"
              :autoValidar="false"
              @resultado="r => onResultado('cc', r)"
            />
            <!-- F002 -->
            <F002pdfValidador
              v-if="v.id === 'f002'"
              ref="refF002"
              :idTramite="p_idTramite"
              :numero="p_numero"
              :autoValidar="false"
              @resultado="r => onResultado('f002', r)"
            />
            <!-- F012A -->
            <F012ApdfValidador
              v-if="v.id === 'f012a'"
              ref="refF012a"
              :idTramite="p_idTramite"
              :numero="p_numero"
              :autoValidar="false"
              @resultado="r => onResultado('f012a', r)"
            />
            <!-- F012B -->
            <F012BpdfValidador
              v-if="v.id === 'f012b'"
              ref="refF012b"
              :idTramite="p_idTramite"
              :numero="p_numero"
              :autoValidar="false"
              @resultado="r => onResultado('f012b', r)"
            />
            <!-- F010A -->
            <F010ApdfValidador
              v-if="v.id === 'f010a'"
              ref="refF010a"
              :idTramite="p_idTramite"
              :numero="p_numero"
              :autoValidar="false"
              @resultado="r => onResultado('f010a', r)"
            />
            <!-- F053 -->
            <F053pdfValidador
              v-if="v.id === 'f053'"
              ref="refF053"
              :idTramite="p_idTramite"
              :numero="p_numero"
              :autoValidar="false"
              @resultado="r => onResultado('f053', r)"
            />
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import CVpdfValidador    from "@/components/CVpdfValidador.vue";
import CCpdfValidador    from "@/components/CCpdfValidador.vue";
import F002pdfValidador  from "@/components/F002pdfValidador.vue";
import F012ApdfValidador from "@/components/F012ApdfValidador.vue";
import F012BpdfValidador from "@/components/F012BpdfValidador.vue";
import F010ApdfValidador from "@/components/F010ApdfValidador.vue";
import F053pdfValidador from "@/components/F053pdfValidador.vue";

export default {
  name: "ValidacionDocumentos",
  components: { CVpdfValidador, CCpdfValidador, F002pdfValidador, F012ApdfValidador, F012BpdfValidador, F010ApdfValidador, F053pdfValidador },

  props: {
    p_idTramite:  { type: Number, default: null },
    p_idPlanilla: { type: Number, default: null },
    p_numero:     { type: String, default: null },
  },

  data() {
    return {
      tabActiva:    null,
      validandoTodo: false,
      validaciones: [
        { id: 'cv',    sigla: 'CV',   label: 'Código de Validación (CV)',     estado: null, validando: false },
        { id: 'cc',    sigla: 'CC',   label: 'Consulta de Cobertura (CC)',    estado: null, validando: false },
        { id: 'f002',  sigla: '002',  label: 'Formulario 002',                estado: null, validando: false },
        { id: 'f012a', sigla: '012A', label: 'Formulario 012A',               estado: null, validando: false },
        { id: 'f012b', sigla: '012B', label: 'Formulario 012B',               estado: null, validando: false },
        { id: 'f010a', sigla: '010A', label: 'Formulario 010A',               estado: null, validando: false },
        { id: 'f053',  sigla: '053',  label: 'Formulario 053',                estado: null, validando: false },
      ],
    };
  },

  methods: {
    // ── Toggle acordeón ───────────────────────────────────────
    toggleTab(id) {
      this.tabActiva = this.tabActiva === id ? null : id;
    },

    // ── Recibe resultado de cada componente hijo ───────────────
    // estado: 'ok' | 'leve' | 'error' | 'sin-archivo'
    onResultado(id, estado) {
      const v = this.validaciones.find(v => v.id === id);
      if (v) {
        v.estado   = estado;
        v.validando = false;
      }
    },

    // ── Validar individual ────────────────────────────────────
    async validarIndividual(id) {
      const v = this.validaciones.find(v => v.id === id);
      if (!v) return;
      v.validando = true;
      if (this.tabActiva !== id) this.tabActiva = id;

      await this.$nextTick();

      const refMap = {
        cv:    this.$refs.refCV?.[0]    ?? this.$refs.refCV,
        cc:    this.$refs.refCC?.[0]    ?? this.$refs.refCC,
        f002:  this.$refs.refF002?.[0]  ?? this.$refs.refF002,
        f012a: this.$refs.refF012a?.[0] ?? this.$refs.refF012a,
        f012b: this.$refs.refF012b?.[0] ?? this.$refs.refF012b,
        f010a: this.$refs.refF010a?.[0] ?? this.$refs.refF010a,
        f053:  this.$refs.refF053?.[0]  ?? this.$refs.refF053,
      };
      const ref = refMap[id];
      if (ref?.ejecutarValidacion) {
        await ref.ejecutarValidacion();
      } else {
        v.validando = false;
      }
    },

    // ── Validar todo ──────────────────────────────────────────
    async validarTodo() {
      this.validandoTodo = true;
      for (const v of this.validaciones) {
        await this.validarIndividual(v.id);
      }
      this.validandoTodo = false;
      this.tabActiva = null;
    },

    // ── Limpiar ───────────────────────────────────────────────
    limpiarTodo() {
      this.validaciones.forEach(v => { v.estado = null; v.validando = false; });
      this.tabActiva = null;
    },

    // ── Clases CSS según estado ───────────────────────────────
    headerClase(v) {
      if (!v.estado) return 'bg-light text-dark';
      return {
        ok:          'header-ok',
        leve:        'header-leve',
        error:       'header-error',
        'sin-archivo': 'header-sin-archivo',
      }[v.estado] ?? 'bg-light text-dark';
    },

    estadoBadgeClase(v) {
      if (!v.estado) return 'bg-secondary text-white';
      return {
        ok:            'bg-success text-white',
        leve:          'bg-warning text-dark',
        error:         'bg-danger text-white',
        'sin-archivo': 'bg-secondary text-white',
      }[v.estado] ?? 'bg-secondary text-white';
    },

    badgeClase(v) {
      if (!v.estado) return 'bg-secondary';
      return {
        ok:            'bg-success',
        leve:          'bg-warning text-dark',
        error:         'bg-danger',
        'sin-archivo': 'bg-secondary',
      }[v.estado] ?? 'bg-secondary';
    },

    botonIndividualClase(v) {
      if (!v.estado) return 'btn-outline-primary';
      return {
        ok:            'btn-outline-success',
        leve:          'btn-outline-warning',
        error:         'btn-outline-danger',
        'sin-archivo': 'btn-outline-secondary',
      }[v.estado] ?? 'btn-outline-primary';
    },

    iconoEstado(v) {
      if (v.validando) return 'hourglass_empty';
      if (!v.estado)   return 'help_outline';
      return {
        ok:            'check_circle',
        leve:          'warning',
        error:         'cancel',
        'sin-archivo': 'folder_off',
      }[v.estado] ?? 'help_outline';
    },

    textoEstado(v) {
      if (v.validando) return 'Validando...';
      if (!v.estado)   return 'Pendiente';
      return {
        ok:            'Sin novedades',
        leve:          'Novedades leves',
        error:         'Con novedades',
        'sin-archivo': 'Sin archivo',
      }[v.estado] ?? 'Pendiente';
    },
  },
};
</script>

<style scoped>
/* ── Headers de acordeón ─────────────────────────────────────── */
.header-ok {
  background-color: #d1e7dd !important;
  color: #0a3622 !important;
}
.header-leve {
  background-color: #fff3cd !important;
  color: #664d03 !important;
}
.header-error {
  background-color: #f8d7da !important;
  color: #58151c !important;
}
.header-sin-archivo {
  background-color: #e2e3e5 !important;
  color: #41464b !important;
}

/* ── Acordeón ────────────────────────────────────────────────── */
.accordion-button::after { display: none; }
.accordion-button:not(.collapsed) { box-shadow: none; }
.accordion-collapse { display: none; }
.accordion-collapse.show { display: block; }

/* ── Transición suave ────────────────────────────────────────── */
.accordion-item {
  transition: box-shadow 0.2s;
}
.accordion-item:hover {
  box-shadow: 0 2px 8px rgba(0,0,0,0.1) !important;
}
</style>